/**
 * BrowserRecordingStep Component
 *
 * Recording step for the NewReflectionModal that allows users to record
 * audio directly in the browser. Features:
 * - Real-time decibel meter
 * - Recording duration display
 * - Chunk upload status
 * - Wake lock indicator
 * - Browser compatibility warnings
 */

import { useEffect, useState, useRef } from "react";
import {
  Mic,
  MicOff,
  Square,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DecibelMeter } from "@/components/audio/DecibelMeter";
import { cn } from "@/lib/utils";
import type {
  BrowserRecordingState,
  BrowserCompatibility,
} from "@/types/browser-recording";
import type { RecordingSessionState } from "@/hooks/useBrowserRecordingSession";

interface BrowserRecordingStepProps {
  /** Browser recording state */
  recordingState: BrowserRecordingState;
  /** Session state */
  sessionState: RecordingSessionState;
  /** Browser compatibility info */
  compatibility: BrowserCompatibility;
  /** Called when user clicks start */
  onStart: () => Promise<void>;
  /** Called when user clicks stop */
  onStop: () => Promise<void>;
  /** Called when user wants to cancel */
  onCancel: () => void;
}

/**
 * Format duration as MM:SS
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Get status color based on recording status
 */
function getStatusColor(status: BrowserRecordingState["status"]): string {
  switch (status) {
    case "recording":
      return "text-red-500";
    case "paused":
      return "text-yellow-500";
    case "completed":
      return "text-green-500";
    case "error":
      return "text-red-600 dark:text-red-400";
    default:
      return "text-muted-foreground";
  }
}

export function BrowserRecordingStep({
  recordingState,
  sessionState,
  compatibility,
  onStart,
  onStop,
  onCancel,
}: BrowserRecordingStepProps) {
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Check for no audio warning
  const [noAudioWarning, setNoAudioWarning] = useState(false);
  const noAudioTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Show warning if no audio detected for 10 seconds during recording
    if (recordingState.status === "recording") {
      if (recordingState.audioLevel.normalized < 0.01) {
        if (!noAudioTimeoutRef.current) {
          noAudioTimeoutRef.current = window.setTimeout(() => {
            setNoAudioWarning(true);
          }, 10000);
        }
      } else {
        if (noAudioTimeoutRef.current) {
          clearTimeout(noAudioTimeoutRef.current);
          noAudioTimeoutRef.current = null;
        }
        setNoAudioWarning(false);
      }
    } else {
      if (noAudioTimeoutRef.current) {
        clearTimeout(noAudioTimeoutRef.current);
        noAudioTimeoutRef.current = null;
      }
      setNoAudioWarning(false);
    }

    return () => {
      if (noAudioTimeoutRef.current) {
        clearTimeout(noAudioTimeoutRef.current);
      }
    };
  }, [recordingState.status, recordingState.audioLevel.normalized]);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await onStart();
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await onStop();
    } finally {
      setIsStopping(false);
    }
  };

  // Browser not supported
  if (!compatibility.mediaRecorder) {
    return (
      <div className="py-8 text-center">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Your browser doesn't support audio recording. Please use Chrome,
            Firefox, Safari, or Edge.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Error state
  if (recordingState.status === "error" || sessionState.sessionError) {
    const errorMessage =
      recordingState.error || sessionState.sessionError || "Unknown error";
    return (
      <div className="py-8 space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
        <div className="flex justify-center">
          <Button variant="outline" onClick={onCancel}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Idle state - ready to start
  if (
    recordingState.status === "idle" ||
    recordingState.status === "requesting_permission"
  ) {
    return (
      <div className="py-8 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-primary/10 mb-4">
            <Mic className="h-10 w-10 text-primary" />
          </div>
          <h3 className="text-lg font-medium">Record Your Conversation</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Click the button below to start recording. Your conversation will be
            automatically uploaded and transcribed.
          </p>
        </div>

        {!compatibility.wakeLock && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your browser doesn't support wake lock. Please keep this tab
              active and your screen on during recording.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleStart}
            disabled={
              isStarting ||
              sessionState.isInitializing ||
              recordingState.status === "requesting_permission"
            }
            className="gap-2"
          >
            {isStarting || sessionState.isInitializing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Mic className="h-5 w-5" />
                Start Recording
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Recording or paused state
  if (
    recordingState.status === "recording" ||
    recordingState.status === "paused"
  ) {
    return (
      <div className="py-6 space-y-6">
        {/* Recording indicator */}
        <div className="flex items-center justify-center gap-3">
          <div
            className={cn(
              "h-3 w-3 rounded-full",
              recordingState.status === "recording"
                ? "bg-red-500 dark:bg-red-600 animate-pulse"
                : "bg-yellow-500 dark:bg-yellow-600"
            )}
          />
          <span
            className={cn(
              "text-lg font-medium",
              getStatusColor(recordingState.status)
            )}
          >
            {recordingState.status === "recording" ? "Recording" : "Paused"}
          </span>
          <span className="text-2xl font-mono font-bold">
            {formatDuration(recordingState.duration)}
          </span>
        </div>

        {/* Decibel meter */}
        <div className="px-4">
          <p className="text-sm text-muted-foreground mb-2 text-center">
            Microphone Level
          </p>
          <DecibelMeter
            audioLevel={recordingState.audioLevel}
            showLabels={true}
            size="lg"
          />
        </div>

        {/* No audio warning */}
        {noAudioWarning && (
          <Alert variant="destructive">
            <MicOff className="h-4 w-4" />
            <AlertDescription>
              No audio detected. Please check your microphone is working.
            </AlertDescription>
          </Alert>
        )}

        {/* Status indicators */}
        <div className="flex justify-center gap-6 text-sm">
          {/* Chunks uploaded */}
          <div className="flex items-center gap-2">
            {recordingState.chunksRecorded > 0 ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            <span className="text-muted-foreground">
              {recordingState.chunksUploaded} / {recordingState.chunksRecorded}{" "}
              chunks saved
            </span>
          </div>

          {/* Wake lock status */}
          {compatibility.wakeLock && (
            <div className="flex items-center gap-2">
              {recordingState.wakeLockActive ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              )}
              <span className="text-muted-foreground">
                {recordingState.wakeLockActive
                  ? "Screen active"
                  : "Keep tab open"}
              </span>
            </div>
          )}
        </div>

        {/* Keep tab open warning */}
        <p className="text-xs text-center text-muted-foreground">
          Keep this tab open during recording. Your audio is being saved
          automatically.
        </p>

        {/* Stop button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            variant="destructive"
            onClick={handleStop}
            disabled={isStopping || sessionState.isCompleting}
            className="gap-2"
          >
            {isStopping || sessionState.isCompleting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Stopping...
              </>
            ) : (
              <>
                <Square className="h-5 w-5" />
                Stop Recording
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Stopping state
  if (
    recordingState.status === "stopping" ||
    recordingState.status === "uploading"
  ) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-muted-foreground">
          {recordingState.status === "stopping"
            ? "Stopping recording..."
            : "Uploading final chunks..."}
        </p>
      </div>
    );
  }

  // Completed state
  if (recordingState.status === "completed") {
    return (
      <div className="py-8 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <p className="text-lg font-medium text-green-800 dark:text-green-200">
          Recording Complete!
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Duration: {formatDuration(recordingState.duration)} •{" "}
          {recordingState.chunksUploaded} chunks saved
        </p>
      </div>
    );
  }

  // Default fallback
  return null;
}
